<template>
  <div class="time-series-chart" :class="{ compact }">
    <!-- COMPACT MODE -->
    <template v-if="compact">
      <svg 
        width="100%" 
        height="30" 
        viewBox="0 0 100 30" 
        preserveAspectRatio="none"
        class="mini-chart"
      >
        <polyline 
          :points="miniLinePoints" 
          fill="none" 
          class="mini-line"
          stroke-width="2"
        />
      </svg>
    </template>

    <!-- FULL MODE -->
    <template v-else>
      <div v-if="title" class="chart-title">{{ title }}</div>
      
      <div v-if="showNote && hasConfidence" class="chart-note">
        {{ $t('Showing mean values with ± standard deviation confidence interval') }}
      </div>
      
      <div class="chart-container">
        <svg 
          width="100%" 
          height="200" 
          viewBox="0 0 300 200"
        >
          <!-- Confidence gradient -->
          <defs v-if="hasConfidence">
            <linearGradient id="confidenceGradient" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stop-color="currentColor" stop-opacity="0.2" />
              <stop offset="100%" stop-color="currentColor" stop-opacity="0.05" />
            </linearGradient>
          </defs>
          
          <!-- Background -->
          <rect width="300" height="160" class="chart-bg" />
          
          <!-- Y-axis labels -->
          <text 
            v-for="label in yLabels" 
            :key="label.key" 
            x="10" 
            :y="label.y" 
            class="axis-label"
          >
            {{ label.text }}
          </text>
          
          <!-- Grid lines -->
          <template v-if="showGrid">
            <line 
              v-for="line in gridLines" 
              :key="line.key"
              x1="30" 
              :y1="line.y" 
              x2="270" 
              :y2="line.y" 
              class="grid-line"
            />
          </template>
          
          <!-- Confidence interval area -->
          <path 
            v-if="hasConfidence"
            :d="confidencePath" 
            class="confidence-area"
          />
          
          <!-- Bound lines (dashed) -->
          <template v-if="hasConfidence && showBounds">
            <polyline :points="upperBoundPoints" class="bound-line" />
            <polyline :points="lowerBoundPoints" class="bound-line" />
          </template>
          
          <!-- Main line -->
          <polyline :points="linePoints" class="main-line" />
          
          <!-- Data points -->
          <circle 
            v-for="(point, index) in points"
            :key="index"
            :cx="point.x" 
            :cy="point.y"
            r="3"
            class="data-point"
          >
            <title>{{ point.label }}</title>
          </circle>
          
          <!-- X-axis labels -->
          <text 
            v-for="label in xLabels" 
            :key="label.key"
            :x="label.x" 
            y="180" 
            class="axis-label x-label"
          >
            {{ label.text }}
          </text>
        </svg>
        
        <!-- Summary -->
        <div v-if="showSummary" class="chart-summary">
          <div 
            v-for="stat in summaryItems" 
            :key="stat.label" 
            class="summary-item"
          >
            <span class="summary-label">{{ stat.label }}</span>
            <span class="summary-value">{{ stat.value }}</span>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
export default {
  name: 'TimeSeriesChart',
  
  props: {
    data: {
      type: Array,
      required: true,
      validator: data => data.every(item => 
        'time_interval' in item && ('value' in item || 'mean' in item)
      )
    },
    title: { type: String, default: '' },
    unit: { type: String, default: '' },
    decimals: { type: Number, default: 2 },
    showGrid: { type: Boolean, default: true },
    showSummary: { type: Boolean, default: true },
    showNote: { type: Boolean, default: true },
    showBounds: { type: Boolean, default: false },
    compact: { type: Boolean, default: false }
  },

  computed: {
    // Layout constants
    width() { return 240 },
    height() { return 140 },
    offsetX() { return 30 },
    offsetY() { return 20 },

    // Data analysis
    hasConfidence() {
      return this.data.length > 0 && 'mean' in this.data[0] && 'stdev' in this.data[0]
    },

    values() {
      return this.data.map(item => this.hasConfidence ? item.mean : item.value)
    },

    minValue() {
      const vals = this.hasConfidence 
        ? this.data.map(item => item.mean - item.stdev)
        : this.values
      return Math.min(...vals) * 0.95
    },

    maxValue() {
      const vals = this.hasConfidence 
        ? this.data.map(item => item.mean + item.stdev)
        : this.values
      return Math.max(...vals) * 1.05
    },

    midValue() {
      return (this.maxValue + this.minValue) / 2
    },

    range() {
      return this.maxValue - this.minValue || 1
    },

    average() {
      return this.values.reduce((sum, val) => sum + val, 0) / this.values.length
    },

    averageStdDev() {
      if (!this.hasConfidence) return 0
      const stdevs = this.data.map(item => item.stdev)
      return stdevs.reduce((sum, val) => sum + val, 0) / stdevs.length
    },

    timeRange() {
      if (!this.data.length) return this.$t('No data')
      const first = this.formatYear(this.data[0].time_interval[0])
      const last = this.formatYear(this.data[this.data.length - 1].time_interval[1])
      return `${first}-${last}`
    },

    // SVG elements - Full mode
    yLabels() {
      return [
        { key: 'max', y: 20, text: this.format(this.maxValue) },
        { key: 'mid', y: 20 + this.height / 2, text: this.format(this.midValue) },
        { key: 'min', y: 20 + this.height - 5, text: this.format(this.minValue) }
      ]
    },

    gridLines() {
      return [
        { key: 'top', y: 20 },
        { key: 'mid', y: 20 + this.height / 2 }
      ]
    },

    points() {
      return this.data.map((item, index) => ({
        x: this.getX(index),
        y: this.getY(this.hasConfidence ? item.mean : item.value),
        label: this.getPointLabel(item)
      }))
    },

    linePoints() {
      return this.points.map(p => `${p.x},${p.y}`).join(' ')
    },

    upperBoundPoints() {
      if (!this.hasConfidence) return ''
      return this.data.map((item, index) => 
        `${this.getX(index)},${this.getY(item.mean + item.stdev)}`
      ).join(' ')
    },

    lowerBoundPoints() {
      if (!this.hasConfidence) return ''
      return this.data.map((item, index) => 
        `${this.getX(index)},${this.getY(item.mean - item.stdev)}`
      ).join(' ')
    },

    confidencePath() {
      if (!this.hasConfidence) return ''

      const upper = this.data.map((item, i) => {
        const x = this.getX(i)
        const y = this.getY(item.mean + item.stdev)
        return i === 0 ? `M ${x},${y}` : `L ${x},${y}`
      }).join(' ')

      const lower = this.data.slice().reverse().map((item, i) => {
        const x = this.getX(this.data.length - 1 - i)
        const y = this.getY(item.mean - item.stdev)
        return `L ${x},${y}`
      }).join(' ')

      return `${upper} ${lower} Z`
    },

    xLabels() {
      const maxLabels = 8
      const step = Math.ceil(this.data.length / maxLabels)
      return this.data
        .filter((_, i) => i % step === 0)
        .map((item, i) => ({
          key: `x-${i}`,
          x: this.getX(i * step),
          text: this.formatYear(item.time_interval[0])
        }))
    },

    summaryItems() {
      const items = [
        { label: this.$t('Data Points'), value: this.data.length },
        { label: this.$t('Time Range'), value: this.timeRange },
        { label: `${this.hasConfidence ? this.$t('Mean') : this.$t('Avg')} ${this.unit || ''}`.trim(), value: this.format(this.average) }
      ]
      if (this.hasConfidence) {
        items.push({ label: this.$t('Avg StdDev'), value: this.format(this.averageStdDev) })
      }
      return items
    },

    // Compact mode
    miniLinePoints() {
      if (!this.data?.length) return ''
      
      const values = this.data.map(item => item.mean ?? item.value ?? 0)
      const minVal = Math.min(...values) * 0.95
      const maxVal = Math.max(...values) * 1.05
      const valRange = maxVal - minVal || 1
      
      return this.data.map((item, index) => {
        const x = index * (100 / Math.max(this.data.length - 1, 1))
        const value = item.mean ?? item.value ?? 0
        const y = 28 - ((value - minVal) / valRange) * 24
        return `${x},${y}`
      }).join(' ')
    }
  },

  methods: {
    getX(index) {
      const step = this.width / Math.max(this.data.length - 1, 1)
      return this.offsetX + index * step
    },

    getY(value) {
      const normalized = (value - this.minValue) / this.range
      return this.offsetY + this.height - normalized * this.height
    },

    format(value) {
      return value?.toFixed(this.decimals) ?? 'N/A'
    },

    formatYear(dateString) {
      return new Date(dateString).getFullYear()
    },

    getPointLabel(item) {
      const year = this.formatYear(item.time_interval[0])
      if (this.hasConfidence) {
        return `${year}: ${this.format(item.mean)} ± ${this.format(item.stdev)} ${this.unit}`
      }
      return `${year}: ${this.format(item.value)} ${this.unit || ''}`
    }
  }
}
</script>

<style lang="scss" scoped>
.time-series-chart {
  width: 100%;
}

// ================================
// COMPACT MODE
// ================================
.time-series-chart.compact {
  background: $grey-2;
  border-radius: var(--radius-sm);
  padding: 0.125rem;

  body.body--dark & {
    background: $grey-9;
  }
}

.mini-chart {
  display: block;
}

.mini-line {
  stroke: $primary;

  body.body--dark & {
    stroke: $secondary;
  }
}

// ================================
// FULL MODE
// ================================
.chart-title {
  font-size: 0.875rem;
  font-weight: 600;
  color: $grey-8;
  margin-bottom: 0.75rem;
  text-align: center;

  body.body--dark & {
    color: $grey-4;
  }
}

.chart-note {
  padding: 0.5rem;
  background: $grey-2;
  border-radius: var(--radius-sm);
  margin-bottom: 0.5rem;
  font-size: 0.6875rem;
  color: $grey-7;
  text-align: center;

  body.body--dark & {
    background: $grey-9;
    color: $grey-5;
  }
}

.chart-container {
  background: white;
  border: 1px solid $grey-4;
  border-radius: var(--radius-md);
  padding: 1rem;

  body.body--dark & {
    background: $dark;
    border-color: $grey-8;
  }
}

// SVG elements
.chart-bg {
  fill: $grey-2;
  stroke: $grey-4;
  stroke-width: 1;

  body.body--dark & {
    fill: $grey-9;
    stroke: $grey-7;
  }
}

.axis-label {
  font-size: 10px;
  fill: $grey-7;

  body.body--dark & {
    fill: $grey-5;
  }

  &.x-label {
    text-anchor: middle;
    font-size: 9px;
  }
}

.grid-line {
  stroke: $grey-4;
  stroke-width: 1;
  stroke-dasharray: 2, 2;

  body.body--dark & {
    stroke: $grey-7;
  }
}

.confidence-area {
  fill: $primary;
  opacity: 0.15;

  body.body--dark & {
    fill: $secondary;
    opacity: 0.2;
  }
}

.bound-line {
  fill: none;
  stroke: $primary;
  stroke-width: 1;
  stroke-dasharray: 3, 3;
  opacity: 0.6;

  body.body--dark & {
    stroke: $secondary;
  }
}

.main-line {
  fill: none;
  stroke: $primary;
  stroke-width: 2;

  body.body--dark & {
    stroke: $secondary;
  }
}

.data-point {
  fill: $primary;
  stroke: white;
  stroke-width: 1.5;
  cursor: pointer;
  transition: r 0.15s ease;

  &:hover {
    r: 5;
  }

  body.body--dark & {
    fill: $secondary;
    stroke: $dark;
  }
}

// Summary
.chart-summary {
  display: flex;
  justify-content: space-around;
  background: $grey-2;
  border-radius: var(--radius-sm);
  padding: 0.75rem;
  margin-top: 0.375rem;
  flex-wrap: wrap;
  gap: 0.5rem;

  body.body--dark & {
    background: $grey-9;
  }
}

.summary-item {
  text-align: center;
}

.summary-label {
  display: block;
  font-size: 0.6875rem;
  color: $grey-7;
  margin-bottom: 0.125rem;

  body.body--dark & {
    color: $grey-5;
  }
}

.summary-value {
  font-size: 0.8125rem;
  font-weight: 600;
  color: $grey-9;

  body.body--dark & {
    color: $grey-3;
  }
}

// ================================
// RESPONSIVE
// ================================
@media (max-width: 48rem), (max-height: 48rem)  {
  .summary-item {
    flex: 1 1 40%;
  }
}
</style>